// Legal Widget — create a niche by cloning.
//
// A niche is never created from scratch — it is cloned from an existing one so
// it inherits a complete, compliant legal / consent / footer content set, then
// is re-pointed at a new brand. Form:
//   - source niche  (dropdown from GET /api/v1/admin/niches)
//   - newSlug       (validated client-side against ^[a-z0-9-]+$; server re-checks)
//   - displayName / defaultBrandName / defaultSupportEmail / defaultApex (overrides)
// On submit -> POST /api/v1/admin/niches/clone -> navigate to the new editor.
// Clone errors (404 source missing / 409 slug taken / 400 invalid) surface inline.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { legalApi, type NicheSummary } from "../../lib/legalApi";
import {
  buttonStyle,
  Card,
  FieldLabel,
  inputStyle,
  PageHeader,
  tokens,
} from "../../lib/theme";

const SLUG_RE = /^[a-z0-9-]+$/;

export default function LegalNicheNew() {
  const navigate = useNavigate();
  const [sources, setSources] = useState<NicheSummary[]>([]);
  const [fromSlug, setFromSlug] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [defaultBrandName, setDefaultBrandName] = useState("");
  const [defaultSupportEmail, setDefaultSupportEmail] = useState("");
  const [defaultApex, setDefaultApex] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    legalApi
      .listNiches()
      .then((rows) => {
        if (cancelled) return;
        setSources(rows);
        if (rows.length > 0 && rows[0]) setFromSlug(rows[0].slug);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const slugValid = SLUG_RE.test(newSlug);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!fromSlug) {
      setError("Pick a source niche to clone from.");
      return;
    }
    if (!slugValid) {
      setError(
        "New slug must match ^[a-z0-9-]+$ (lowercase letters, digits, hyphens).",
      );
      return;
    }

    setSubmitting(true);
    try {
      const created = await legalApi.cloneNiche({
        fromSlug,
        newSlug,
        overrides: {
          // Only send non-empty overrides; blanks fall back to the source's values.
          ...(displayName ? { displayName } : {}),
          ...(defaultBrandName ? { defaultBrandName } : {}),
          ...(defaultSupportEmail ? { defaultSupportEmail } : {}),
          ...(defaultApex ? { defaultApex } : {}),
        },
      });
      navigate(`/legal/${encodeURIComponent(created.slug)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to clone");
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="New niche"
        subtitle="Clone an existing niche, then re-point it at a new brand."
      />
      <Card>
        <form onSubmit={onSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: tokens.space.lg,
            }}
          >
            <div>
              <FieldLabel htmlFor="clone-from">Source niche (clone from)</FieldLabel>
              <select
                id="clone-from"
                value={fromSlug}
                onChange={(e) => setFromSlug(e.target.value)}
                style={inputStyle}
              >
                {sources.length === 0 ? (
                  <option value="">No niches available</option>
                ) : (
                  sources.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.displayName} ({s.slug})
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <FieldLabel
                htmlFor="clone-slug"
                hint="Lowercase letters, digits, hyphens (^[a-z0-9-]+$)."
              >
                New slug
              </FieldLabel>
              <input
                id="clone-slug"
                required
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder="e.g. aca"
                style={{
                  ...inputStyle,
                  borderColor:
                    newSlug && !slugValid
                      ? tokens.color.dangerBorder
                      : tokens.color.borderMid,
                }}
              />
            </div>
            <div>
              <FieldLabel htmlFor="clone-display" hint="Optional — defaults to source.">
                Display name
              </FieldLabel>
              <input
                id="clone-display"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. ACA Health"
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel
                htmlFor="clone-brand"
                hint="Optional — fallback if hostname derive fails."
              >
                Default brand name
              </FieldLabel>
              <input
                id="clone-brand"
                value={defaultBrandName}
                onChange={(e) => setDefaultBrandName(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel htmlFor="clone-email" hint="Optional.">
                Default support email
              </FieldLabel>
              <input
                id="clone-email"
                type="email"
                value={defaultSupportEmail}
                onChange={(e) => setDefaultSupportEmail(e.target.value)}
                placeholder="support@example.com"
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel htmlFor="clone-apex" hint="Optional.">
                Default apex domain
              </FieldLabel>
              <input
                id="clone-apex"
                value={defaultApex}
                onChange={(e) => setDefaultApex(e.target.value)}
                placeholder="example.com"
                style={inputStyle}
              />
            </div>
          </div>

          {error && (
            <div
              role="alert"
              style={{
                marginTop: tokens.space.lg,
                padding: tokens.space.md,
                background: tokens.color.dangerBg,
                border: `1px solid ${tokens.color.dangerBorder}`,
                color: tokens.color.danger,
                borderRadius: tokens.radius.sm,
                fontSize: tokens.font.size.sm,
              }}
            >
              {error}
            </div>
          )}

          <div
            style={{
              marginTop: tokens.space.lg,
              display: "flex",
              gap: tokens.space.sm,
            }}
          >
            <button
              type="submit"
              disabled={submitting}
              style={buttonStyle("primary", submitting)}
            >
              {submitting ? "Cloning…" : "Clone niche"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/legal")}
              disabled={submitting}
              style={buttonStyle("ghost", submitting)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
